import { DiscountErrorCode, DISCOUNT_TARGET_TYPES } from '../constants/discountErrorMapper';
import type { Cart, DiscountCodeEntry } from '../types/discount.types';

export interface ValidationResult {
  isValid: boolean;
  errorCode: DiscountErrorCode | null;
  requiresRemoval: boolean;
  discountCodeId?: string;
}

/**
 * Validates a discount code against a cart and returns its valid state
 * and any applicable error code.
 *
 * @param code The discount code string that was applied
 * @param cart The updated cart object from Commercetools
 * @returns ValidationResult predicting if the code should be kept or removed
 */
export const validateAppliedDiscount = (code: string, cart: Cart): ValidationResult => {
  if (!cart || !cart.discountCodes) {
    return {
      isValid: false,
      errorCode: DiscountErrorCode.INVALID_CODE,
      requiresRemoval: false,
    };
  }

  const appliedCode = cart.discountCodes.find(
    (dc: DiscountCodeEntry) =>
      (dc.discountCode?.obj?.code || dc.discountCode?.code)?.toUpperCase() === code.toUpperCase()
  );

  if (!appliedCode) {
    return {
      isValid: false,
      errorCode: DiscountErrorCode.INVALID_CODE,
      requiresRemoval: false,
    };
  }

  const discountCodeId = appliedCode.discountCode?.id;

  // 1. Basic state validation
  if (appliedCode.state !== 'MatchesCart') {
    let errorCode = DiscountErrorCode.DEACTIVATED;
    if (appliedCode.state === 'DoesNotMatchCart') {
      errorCode = DiscountErrorCode.MIN_BASKET_NOT_MET;
    }
    return {
      isValid: false,
      errorCode,
      requiresRemoval: true, // It's attached but invalid, must remove
      discountCodeId,
    };
  }

  // 2. Product specific discount validation
  const cartDiscounts = appliedCode.discountCode?.obj?.cartDiscounts ?? [];
  const isProductSpecific = cartDiscounts.some(
    (cd) => cd.obj?.target?.type === DISCOUNT_TARGET_TYPES.LINE_ITEMS || cd.obj?.target?.type === DISCOUNT_TARGET_TYPES.MULTI_BUY_LINE_ITEMS
  );

  if (isProductSpecific) {
    const cartDiscountIds = cartDiscounts.map((cd) => cd.id);

    // Check if any line items actually received the discount
    const isEffective = cart.lineItems?.some((item) =>
      item.discountedPrice?.includedDiscounts?.some((id) =>
        cartDiscountIds.includes(id.discount.id)
      )
    );

    if (!isEffective) {
      return {
        isValid: false,
        errorCode: DiscountErrorCode.NOT_APPLICABLE,
        requiresRemoval: true, // It's valid but not for anything in cart, must remove
        discountCodeId,
      };
    }
  }

  return {
    isValid: true,
    errorCode: null,
    requiresRemoval: false,
    discountCodeId,
  };
};
